import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { TextField, IconButton, CircularProgress, Snackbar, Alert, Menu, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import EditIcon from '@mui/icons-material/Edit';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import ChatMessage from './ChatMessage';
import ProjectHeader from './ProjectHeader';
import EditModeActionBar from './EditModeActionBar';
import SourcesList from './SourcesList';
import ImportModal from './ImportModal';
import { sendMessage, ChatMessage as OpenAIMessage } from '../../services/openai';
import { api, apiService, RetrievedSource } from '../../services/api';
import { RootState, AppDispatch } from '../../store/store';
import { addMessageAsync, deleteMessagesAsync, updateProjectAsync } from '../../store/projectSlice';
import { Message } from '../../types';
import styles from './ChatInterface.module.css';
import clsx from 'clsx';
import { generateMessagesCSV, downloadCSV } from '../../utils/MessageUtils';
import { createMessage } from '../../utils/ChatUtils';


interface ChatInterfaceProps {
  windowHeight?: number;
}

// Using createMessage from ChatUtils

const ChatInterface: React.FC<ChatInterfaceProps> = ({ windowHeight }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { selectedProject, loading: projectLoading } = useSelector((state: RootState) => state.projects);
  const [newMessage, setNewMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  // We don't need a separate isProjectLoading state since we use the one from Redux
  const [error, setError] = useState<string | null>(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedMessages, setSelectedMessages] = useState<Set<number>>(new Set());
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [retrievedSources, setRetrievedSources] = useState<RetrievedSource[]>([]);
  const [isLoadingSources, setIsLoadingSources] = useState(false);
  const [isRestartDialogOpen, setIsRestartDialogOpen] = useState(false);
  // Using state to indicate restart operation in progress
  const [restartLoading, setRestartLoading] = useState(false);
  // Export dialog state
  const [isExportDialogOpen, setIsExportDialogOpen] = useState(false);
  // Import modal state
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const resizeTimeoutRef = useRef<number>();
  const [isSendingIntroMessage, setIsSendingIntroMessage] = useState(false);

  // Track message count and project ID to determine when to scroll
  const prevMessageCountRef = useRef<number>(0);
  const prevProjectIdRef = useRef<number | undefined>();
  
  const scrollToBottom = useCallback(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'auto' });
    }
  }, []);

  useEffect(() => {
    const handleResize = () => {
      if (resizeTimeoutRef.current) {
        window.cancelAnimationFrame(resizeTimeoutRef.current);
      }
      resizeTimeoutRef.current = window.requestAnimationFrame(scrollToBottom);
    };

    window.addEventListener('resize', handleResize);
    
    // Get current values
    const currentMessageCount = selectedProject?.messages?.length || 0;
    const currentProjectId = selectedProject?.id;
    
    // Scroll in these cases:
    // 1. New messages were added
    // 2. Project was changed
    // 3. Initial load (no messages)
    const projectChanged = currentProjectId !== undefined && currentProjectId !== prevProjectIdRef.current;
    const newMessagesAdded = currentMessageCount > prevMessageCountRef.current;
    
    if (newMessagesAdded || projectChanged || currentMessageCount === 0) {
      scrollToBottom();
    }
    // Update refs unconditionally to avoid stale baselines after deletions/edits
    prevMessageCountRef.current = currentMessageCount;
    prevProjectIdRef.current = currentProjectId;

    return () => {
      window.removeEventListener('resize', handleResize);
      if (resizeTimeoutRef.current) {
        window.cancelAnimationFrame(resizeTimeoutRef.current);
      }
    };
  }, [scrollToBottom, selectedProject?.messages, selectedProject?.id, selectedProject?.base_prompt]);

  // Project loading is handled by the Redux store loading state

  useEffect(() => {
    if (!isLoading && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isLoading]);

  useEffect(() => {
    // Only attempt to add intro message if not already sending one
    if (isSendingIntroMessage) return;
    
    const addIntroMessage = async () => {
      // Only add intro if project exists, has an intro message, and has no messages
      if (selectedProject && 
          selectedProject.intro_message && 
          (!selectedProject.messages || selectedProject.messages.length === 0)) {
        
        setIsSendingIntroMessage(true);
        
        try {
          const message = createMessage(
            selectedProject.intro_message,
            true,
            'Introduction'
          );
          
          await dispatch(addMessageAsync({
            projectId: selectedProject.id as number,
            message
          })).unwrap();
          
        } catch (error) {
          console.error('Failed to add intro message:', error);
        } finally {
          setIsSendingIntroMessage(false);
        }
      }
    };
    
    addIntroMessage();
  }, [selectedProject, dispatch]);

  const convertToOpenAIMessages = (messages: Message[]): OpenAIMessage[] => {
    return messages.map(msg => ({
      role: msg.isBot ? 'assistant' : 'user',
      content: msg.text
    }));
  };

  const formatBedrockMessages = (messages: Message[], sources?: RetrievedSource[]) => {
    const formatted = messages.map(msg => ({
      role: msg.isBot ? 'assistant' : 'user',
      content: [
        {
          type: 'text',
          text: msg.text
        }
      ]
    }));

    if (selectedProject?.base_prompt) {
      formatted.unshift({
        role: 'system',
        content: [
          {
            type: 'text',
            text: selectedProject.base_prompt
          }
        ]
      });
    }

    const lastUserIndex = formatted.length - 1;
    
    if (sources && sources.length > 0) {
      const sourcesText = sources
        .map((source, index) => {
          const title = source.metadata?.title || `Source ${index + 1}`;
          return `SOURCE ${index + 1}: ${title}\n${source.content}`;
        })
        .join('\n\n');

      formatted.splice(lastUserIndex, 0, {
        role: 'user',
        content: [
          {
            type: 'text',
            text: `SOURCES:\n${sourcesText}`
          }
        ]
      });
    } else {
      formatted.splice(lastUserIndex, 0, {
        role: 'user',
        content: [
          {
            type: 'text',
            text: `SOURCES:\nNone found`
          }
        ]
      });
    }

    return formatted;
  };

  const retrieveRagSources = async (query: string): Promise<RetrievedSource[]> => {
    if (!selectedProject?.rag_vector_db) {
      throw new Error('RAG configuration is incomplete');
    }

    setIsLoadingSources(true);
    try {
      const sources = await apiService.rag.retrieveDocuments(
        query,
        selectedProject.rag_vector_db,
        selectedProject.rag_top_k || 3,
        selectedProject.rag_confidence_threshold || 0.7
      );
      return sources;
    } catch (error) {
      console.error('Error retrieving RAG sources:', error);
      throw error;
    } finally {
      setIsLoadingSources(false);
    }
  };

  const sendBedrockMessage = async (messages: any[], model: string, maxTokens: number = 500, temperature: number = 0.7) => {
    try {
      const response = await api.post('/bedrock/completions', {
        messages,
        model,
        maxTokens,
        temperature
      });
      return response.data.content[0].text;
    } catch (error) {
      console.error('Error calling Bedrock API:', error);
      throw error;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedProject || isLoading) return;

    const userMessage = createMessage(newMessage, false, 'You');
    setNewMessage('');
    setIsLoading(true);
    setRetrievedSources([]);

    try {
      if (selectedProject.id === undefined) {
        throw new Error('Project ID is undefined');
      }

      await dispatch(addMessageAsync({ 
        projectId: selectedProject.id, 
        message: userMessage 
      }));
      
      const messages = [...(selectedProject.messages || []), userMessage];
      
      if (selectedProject.chat_type === 'rag') {
        let sources: RetrievedSource[] = [];
        try {
          sources = await retrieveRagSources(newMessage);
          setRetrievedSources(sources);
        } catch (err) {
          console.error('Error retrieving sources:', err);
        }
        
        const formattedMessages = formatBedrockMessages(messages, sources);
        
        const response = await sendBedrockMessage(
          formattedMessages,
          selectedProject.model_name || 'us.anthropic.claude-3-7-sonnet-20250219-v1:0',
          selectedProject.rag_token_limit || 4000
        );
        
        if (response) {
          const botMessage = createMessage(response, true, selectedProject.model_name || 'Claude-3');
          
          const messageWithSources = {
            ...botMessage,
            sources: sources.map(source => ({
              id: source.id,
              content: source.content,
              score: source.score,
              metadata: source.metadata
            }))
          };
          
          await dispatch(addMessageAsync({ 
            projectId: selectedProject.id, 
            message: messageWithSources 
          }));
        }
      } else {
        const response = await sendMessage(
          convertToOpenAIMessages(messages),
          {
            model_name: selectedProject.model_name || 'gpt-4o',
            base_prompt: selectedProject.base_prompt || ''
          }
        );
        
        if (response) {
          const botMessage = createMessage(response, true, selectedProject.model_name || 'gpt-4o');
          await dispatch(addMessageAsync({ 
            projectId: selectedProject.id, 
            message: botMessage 
          }));
        }
      }
    } catch (error: any) {
      setError(error.message || 'Failed to send message');
    } finally {
      setIsLoading(false);
    }
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handleEditMessages = () => {
    setIsEditMode(true);
    handleMenuClose();
  };

  const handleRestartClick = () => {
    setIsRestartDialogOpen(true);
    handleMenuClose();
  };

  const handleExportMessages = () => {
    setMenuAnchorEl(null); // Close menu
    setIsExportDialogOpen(true);
  };

  const handleImportMessages = () => {
    setMenuAnchorEl(null); // Close menu
    setIsImportModalOpen(true);
  };
  
  const handleImportModalClose = (success?: boolean) => {
    setIsImportModalOpen(false);
    if (success) {
      setError(null);
    }
  };

  const handleExportConfirm = () => {
    setIsExportDialogOpen(false);
    
    if (!selectedProject || !selectedProject.messages || selectedProject.messages.length === 0) {
      setError('No messages to export');
      return;
    }
    
    const csvContent = generateMessagesCSV(selectedProject);
    if (!csvContent) return;
    
    const projectName = selectedProject.name.replace(/\s+/g, '_') || 'export';
    const timestamp = new Date().toISOString().slice(0, 10);
    downloadCSV(csvContent, `${projectName}_messages_${timestamp}.csv`);
  };

  const handleRestartConfirm = async () => {
    if (!selectedProject || !selectedProject.id) return;
    
    setRestartLoading(true); // Track restart operation
    try {
      const messageIds = (selectedProject.messages || [])
        .map(m => m.id)
        .filter(Boolean) as number[];
      
      await api.delete(`projects/${selectedProject.id}/messages`, { 
      data: { messageIds } 
    });
    
    // Reset intro message state - will be sent again on empty project
    setIsSendingIntroMessage(false);
    
    setRetrievedSources([]);
    setIsLoadingSources(false);
      
      dispatch(updateProjectAsync({ id: selectedProject.id, project: {} }));
    } catch (error) {
      console.error('Error restarting chat:', error);
      setError(error instanceof Error ? error.message : 'Failed to restart chat');
    } finally {
      setRestartLoading(false);
      setIsRestartDialogOpen(false);
    }
  };

  const handleMessageSelect = (messageId: number) => {
    const newSelection = new Set(selectedMessages);
    if (newSelection.has(messageId)) {
      newSelection.delete(messageId);
    } else {
      newSelection.add(messageId);
    }
    setSelectedMessages(newSelection);
  };

  const handleDeleteClick = () => {
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    if (selectedProject?.id !== undefined && selectedMessages.size > 0) {
      try {
        await dispatch(deleteMessagesAsync({
          projectId: selectedProject.id,
          messageIds: Array.from(selectedMessages)
        })).unwrap();
        
        setShowDeleteConfirm(false);
        setIsEditMode(false);
        setSelectedMessages(new Set());
        // Ensure we anchor to the bottom after deletions/edit completion
        window.requestAnimationFrame(() => scrollToBottom());
      } catch (error) {
        setError(error instanceof Error ? error.message : 'Failed to delete messages');
      }
    }
  };

  if (!selectedProject) {
    return (
      <div className={styles.chatContainer}>
        <div className={styles.messagesContainer}>
          <div className={styles.loadingContainer}>
            <p>Select or create a project to start chatting</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.chatContainer}>
      <div className={styles.topContainer}>
        <div className={styles.headerContainer}>
          <div className={styles.menuContainer}>
            <IconButton onClick={handleMenuClick} size="small">
              <MoreVertIcon />
            </IconButton>
            <Menu
              anchorEl={menuAnchorEl}
              open={Boolean(menuAnchorEl)}
              onClose={handleMenuClose}
            >
              <MenuItem onClick={handleEditMessages}>
                <EditIcon sx={{ mr: 1 }} />
                Edit Messages
              </MenuItem>
              <MenuItem onClick={handleExportMessages}>
                <FileUploadIcon sx={{ mr: 1, transform: 'rotate(180deg)' }} />
                Export Messages
              </MenuItem>
              <MenuItem onClick={handleImportMessages}>
                <FileUploadIcon sx={{ mr: 1 }} />
                Import Messages
              </MenuItem>
              <MenuItem onClick={handleRestartClick}>
                <RestartAltIcon sx={{ mr: 1 }} />
                Restart Chat
              </MenuItem>
            </Menu>
          </div>
          <ProjectHeader />
        </div>
      </div>
      <div className={clsx(styles.messagesContainer, isEditMode && styles.editMode)}>
        {projectLoading ? (
          <div className={styles.loadingContainer}>
            <CircularProgress size={24} />
          </div>
        ) : (
          (selectedProject?.messages || []).slice().sort((a, b) => 
            new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
          ).map((message, index) => (
            <div key={message.id || index} className={styles.messageRow}>
              {isEditMode && (
                <div className={styles.selectionColumn}>
                  <div 
                    className={clsx(
                      styles.selectionCircle,
                      selectedMessages.has(message.id || -1) && styles.selected
                    )}
                    onClick={() => handleMessageSelect(message.id || -1)}
                  />
                </div>
              )}
              <ChatMessage
                message={message.text}
                sender={message.sender}
                timestamp={new Date(message.timestamp).toLocaleTimeString()}
                isBot={message.isBot}
                messageId={message.id}
                projectId={selectedProject?.id}
                savedRating={message.rating}
                savedComment={message.comment}
              />
              
              {/* Show sources after bot responses - either from state or from message */}
              {message.isBot && selectedProject?.chat_type === 'rag' && (
                <>
                  {/* For the most recent message, use the live sources from state */}
                  {index === (selectedProject?.messages?.length || 0) - 1 && retrievedSources.length > 0 ? (
                    <SourcesList 
                      sources={retrievedSources} 
                      isLoading={isLoadingSources}
                    />
                  ) : (
                    /* For older messages, use sources from the message if available */
                    message.sources && message.sources.length > 0 && (
                      <SourcesList 
                        sources={message.sources} 
                        isLoading={false}
                      />
                    )
                  )}
                </>
              )}
            </div>
          ))
        )}
        {isLoading && (
          <div className={styles.loadingContainer}>
            <CircularProgress size={24} />
          </div>
        )}
        
        {/* Show sources being loaded while waiting for bot response */}
        {isLoadingSources && selectedProject?.chat_type === 'rag' && !retrievedSources.length && (
          <SourcesList 
            sources={[]} 
            isLoading={true} 
          />
        )}
        
        <div ref={messagesEndRef} />
      </div>
      {isEditMode ? (
        <EditModeActionBar
          selectedCount={selectedMessages.size}
          onDelete={handleDeleteClick}
          onCancel={() => {
            setIsEditMode(false);
            setSelectedMessages(new Set());
            // Ensure we anchor to bottom when exiting edit mode without changes
            window.requestAnimationFrame(() => scrollToBottom());
          }}
        />
      ) : (
        <form onSubmit={handleSubmit} className={styles.inputContainer}>
          <div className={styles.inputWrapper}>
            <TextField
              inputRef={inputRef}
              className={styles.messageInput}
              multiline
              maxRows={4}
              placeholder="Type your message..."
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSubmit(e);
                }
              }}
              disabled={isLoading}
              variant="outlined"
              size="small"
              fullWidth
            />
            <IconButton
              className={styles.sendButton}
              type="submit"
              disabled={!newMessage.trim() || isLoading}
              color="primary"
            >
              <SendIcon />
            </IconButton>
          </div>
        </form>
      )}
      <Dialog
        open={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
      >
        <DialogTitle>Delete Messages</DialogTitle>
        <DialogContent>
          Are you sure you want to delete {selectedMessages.size} message{selectedMessages.size !== 1 ? 's' : ''}? This action cannot be undone.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeleteConfirm(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error">Delete</Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={isRestartDialogOpen}
        onClose={() => {
          setIsRestartDialogOpen(false);
          setRestartLoading(false); // Reset loading state when dialog is closed
        }}
      >
        <DialogTitle>Restart Chat</DialogTitle>
        <DialogContent>
          Are you sure you want to restart the conversation? This will delete all messages and start a new conversation.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setIsRestartDialogOpen(false);
            setRestartLoading(false);
          }}>Cancel</Button>
          <Button 
            onClick={handleRestartConfirm} 
            color="error"
            disabled={restartLoading}
            startIcon={restartLoading ? <CircularProgress size={20} /> : null}
          >
            {restartLoading ? 'Restarting...' : 'Restart'}
          </Button>
        </DialogActions>
      </Dialog>
      
      <Dialog
        open={isExportDialogOpen}
        onClose={() => setIsExportDialogOpen(false)}
      >
        <DialogTitle>Export Messages</DialogTitle>
        <DialogContent>
          Are you sure you want to export all messages from this conversation? This will create a CSV file that you can download.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsExportDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleExportConfirm} color="primary">Export</Button>
        </DialogActions>
      </Dialog>
      
      <ImportModal 
        open={isImportModalOpen} 
        onClose={handleImportModalClose}
      />
      
      {error && (
        <Snackbar 
          open={!!error} 
          autoHideDuration={6000} 
          onClose={() => setError(null)}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert onClose={() => setError(null)} severity="error" sx={{ width: '100%' }}>
            {error}
          </Alert>
        </Snackbar>
      )}
    </div>
  );
};

export default ChatInterface;
